<template>
  <my-page :loading="isLoading">
    <template #heading>
      {{ $t("MY_INSTALLATION_HISTORY") }}
    </template>

    <div v-if="history.length" class="entries">
      <installation-history-entry v-for="entry of history" :key="entry.id" :entry="entry" />
    </div>
    <p v-else-if="!isLoading">
      {{ $t('INSTALLATION_HISTORY_EMPTY') }}
    </p>

    <!-- Load More Button -->
    <div v-if="!isLastPage && !isLoadingMore && history.length > 0" class="load-more">
      <button class="load-more-btn" @click="loadMoreHistory">
        {{ $t('LOAD_MORE') }}
      </button>
    </div>

    <!-- Loading indicator for loading more -->
    <div v-if="isLoadingMore" class="loading-more">
      <div class="loading-spinner"></div>
    </div>

    <template #sidebar>
      <p class="g-text">
        {{ $t('INSTALLATION_HISTORY_DESC') }}
      </p>
    </template>
  </my-page>
</template>

<script>
import MyPage from '../components/MyPage.vue';
import InstallationHistoryEntry from './components/InstallationHistoryEntry.vue';

export default {
  components: {
    MyPage,
    InstallationHistoryEntry,
  },
  data() {
    return {
      isLoading: true,
      isLoadingMore: false,
      history: [],
      // Pagination state
      currentPage: 1,
      pageSize: 25,
      isLastPage: false,
    };
  },
  computed: {
    title() {
      return this.$t('MY_INSTALLATION_HISTORY');
    },
  },
  methods: {
    async getHistory(isLoadMore = false) {
      if (isLoadMore) {
        this.isLoadingMore = true;
      } else {
        this.isLoading = true;
      }

      try {
        const start = isLoadMore ? this.history.length : 0;

        const response = await this.$api.getInstallationHistory({
          start,
          length: this.pageSize,
        });

        // Handle both array response and object response with data property
        const newHistory = Array.isArray(response) ? response : (response.data || []);

        if (isLoadMore) {
          // Append new history to existing ones
          this.history = [...this.history, ...newHistory];
        } else {
          // Replace history (initial load)
          this.history = newHistory;
          this.currentPage = 1;
          this.isLastPage = false;
        }

        // Check if this is the last page
        if (newHistory.length < this.pageSize) {
          this.isLastPage = true;
        }
      } catch (error) {
        console.error('Error fetching installation history:', error);
        this.$notify({
          type: 'error',
          title: this.$t('ERROR'),
          text: this.$t('FAILED_TO_LOAD_INSTALLATION_HISTORY'),
        });
      } finally {
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    },

    async loadMoreHistory() {
      if (this.isLoadingMore || this.isLastPage) {
        return;
      }

      this.currentPage++;
      await this.getHistory(true);
    },
  },
  created() {
    this.getHistory();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.entries {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.load-more {
  display: flex;
  justify-content: center;
  padding: 20px;
  margin-top: 20px;
}

.load-more-btn {
  background: var(--button-secondary-bg, #f8f9fa);
  border: 1px solid var(--border-color, #dee2e6);
  color: var(--text-color, #333);
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    background: var(--button-secondary-hover-bg, #e9ecef);
    border-color: var(--border-color-hover, #adb5bd);
  }

  &:active {
    transform: translateY(1px);
  }
}

.loading-more {
  display: flex;
  justify-content: center;
  padding: 20px;
  margin-top: 20px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color, #dee2e6);
  border-top: 2px solid var(--primary-color, #007bff);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
